import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Send, X, Users, Building2, User, Users2, Search, Loader2 } from "lucide-react";

/**
 * Reusable modal for picking publication audience.
 * Props:
 *  - open: boolean
 *  - title: string
 *  - message?: string (optional line)
 *  - defaultTarget?: "all" | "companies" | "users" (default "all")
 *  - defaultCompanyIds?: string[]
 *  - showNotificationToggle?: boolean (default true)
 *  - defaultSendNotification?: boolean (default true)
 *  - defaultEmailSubject?: string
 *  - defaultEmailMessage?: string
 *  - confirmLabel?: string
 *  - onCancel: () => void
 *  - onConfirm: ({ target, company_ids, client_ids, send_notification, send_email, email_subject, email_message }) => Promise<void>|void
 */
export default function PublishTargetModal({
  open,
  title = "Pubblica",
  message,
  defaultTarget = "all",
  defaultCompanyIds = [],
  showNotificationToggle = true,
  defaultSendNotification = true,
  defaultEmailSubject = "",
  defaultEmailMessage = "",
  confirmLabel = "Pubblica",
  onCancel,
  onConfirm,
}) {
  const [target, setTarget] = useState(defaultTarget);
  const [companyIds, setCompanyIds] = useState(new Set(defaultCompanyIds));
  const [clientIds, setClientIds] = useState(new Set());
  const [groupId, setGroupId] = useState("");
  const [sendNotif, setSendNotif] = useState(defaultSendNotification);
  const [sendEmail, setSendEmail] = useState(false);
  const [emailSubject, setEmailSubject] = useState(defaultEmailSubject);
  const [emailMessage, setEmailMessage] = useState(defaultEmailMessage);
  // NEW — independent email audience (decoupled from portal audience).
  // emailTarget: "" (nothing picked) | "same" | "companies" | "users" | "group"
  const [emailTarget, setEmailTarget] = useState("");
  const [emailCompanyIds, setEmailCompanyIds] = useState(new Set());
  const [emailClientIds, setEmailClientIds] = useState(new Set());
  const [emailGroupId, setEmailGroupId] = useState("");
  const [emailUserSearch, setEmailUserSearch] = useState("");
  const [emailCompanySearch, setEmailCompanySearch] = useState("");
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);  // all clients across companies
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    setTarget(defaultTarget);
    setCompanyIds(new Set(defaultCompanyIds));
    setClientIds(new Set());
    setGroupId("");
    setSendNotif(defaultSendNotification);
    setSendEmail(false);
    setEmailSubject(defaultEmailSubject);
    setEmailMessage(defaultEmailMessage);
    setEmailTarget("");
    setEmailCompanyIds(new Set());
    setEmailClientIds(new Set());
    setEmailGroupId("");
    setEmailUserSearch("");
    setEmailCompanySearch("");
    setSearch("");
    (async () => {
      setLoading(true);
      try {
        const [{ data: comps }, { data: usrs }, groupsRes] = await Promise.all([
          api.get("/companies"),
          api.get("/users"),
          api.get("/client-groups").catch(() => ({ data: [] })),
        ]);
        setCompanies(Array.isArray(comps) ? comps : []);
        setUsers(Array.isArray(usrs) ? usrs.filter((u) => u.role === "cliente" && u.status === "active") : []);
        setGroups(Array.isArray(groupsRes.data) ? groupsRes.data : []);
      } catch (e) {
        setCompanies([]);
        setUsers([]);
        setGroups([]);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return companies;
    return companies.filter((c) => (c.name || "").toLowerCase().includes(s));
  }, [companies, search]);

  // Users grouped by company_id → array of { company, users[] }
  const usersByCompany = useMemo(() => {
    const map = new Map();
    for (const u of users) {
      const cid = u.company_id || "__none__";
      if (!map.has(cid)) map.set(cid, []);
      map.get(cid).push(u);
    }
    // Order: companies list order; fallback bucket at end
    const ordered = [];
    for (const c of companies) {
      const list = map.get(c.id);
      if (list && list.length) ordered.push({ company: c, users: list });
    }
    const orphans = map.get("__none__");
    if (orphans && orphans.length) {
      ordered.push({ company: { id: "__none__", name: "— Utenti senza azienda —" }, users: orphans });
    }
    return ordered;
  }, [users, companies]);

  // Count how many users in the currently-selected EMAIL audience have opted-out
  // (email_enabled === false). Shown as an amber banner so the admin knows some
  // recipients will be silently skipped by the backend.
  const emailAudienceStats = useMemo(() => {
    if (!sendEmail) return { total: 0, optedOut: 0 };
    let selectedIds = new Set();
    if (emailTarget === "same") {
      // Mirror portal audience selection
      if (target === "all") {
        selectedIds = new Set(users.map((u) => u.id));
      } else if (target === "companies") {
        selectedIds = new Set(users.filter((u) => companyIds.has(u.company_id)).map((u) => u.id));
      } else if (target === "users") {
        selectedIds = new Set(clientIds);
      } else if (target === "group") {
        const g = groups.find((x) => x.id === groupId);
        const cids = new Set((g && g.company_ids) || []);
        selectedIds = new Set(users.filter((u) => cids.has(u.company_id)).map((u) => u.id));
      }
    } else if (emailTarget === "companies") {
      selectedIds = new Set(users.filter((u) => emailCompanyIds.has(u.company_id)).map((u) => u.id));
    } else if (emailTarget === "users") {
      selectedIds = new Set(emailClientIds);
    } else if (emailTarget === "group") {
      const g = groups.find((x) => x.id === emailGroupId);
      const cids = new Set((g && g.company_ids) || []);
      selectedIds = new Set(users.filter((u) => cids.has(u.company_id)).map((u) => u.id));
    }
    let optedOut = 0;
    for (const u of users) {
      if (selectedIds.has(u.id) && u.email_enabled === false) optedOut += 1;
    }
    return { total: selectedIds.size, optedOut };
  }, [sendEmail, emailTarget, emailCompanyIds, emailClientIds, emailGroupId, target, companyIds, clientIds, groupId, users, groups]);

  const toggleCompany = (id) => {
    setCompanyIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };
  const selectAll = () => setCompanyIds(new Set(filtered.map((c) => c.id)));
  const clearAll = () => setCompanyIds(new Set());
  const toggleUser = (id) => {
    setClientIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };
  const toggleCompanyUsers = (compUsers) => {
    setClientIds((prev) => {
      const n = new Set(prev);
      const all = compUsers.every((u) => n.has(u.id));
      if (all) compUsers.forEach((u) => n.delete(u.id));
      else compUsers.forEach((u) => n.add(u.id));
      return n;
    });
  };
  const clearAllUsers = () => setClientIds(new Set());

  const canConfirm =
    (target === "all" ||
      (target === "companies" && companyIds.size > 0) ||
      (target === "users" && clientIds.size > 0) ||
      (target === "group" && !!groupId)) &&
    // If sendEmail is on → the admin must explicitly pick the email audience
    // (per requirement: "email vuota per default, devono selezionare i destinatari").
    (!sendEmail
      || (emailTarget === "same")
      || (emailTarget === "companies" && emailCompanyIds.size > 0)
      || (emailTarget === "users" && emailClientIds.size > 0)
      || (emailTarget === "group" && !!emailGroupId));

  const submit = async () => {
    if (!canConfirm) return;
    setBusy(true);
    try {
      // Resolve "group" → target companies from the selected group
      let effectiveTarget = target;
      let effectiveCompanyIds = target === "companies" ? Array.from(companyIds) : [];
      if (target === "group" && groupId) {
        const g = groups.find((x) => x.id === groupId);
        effectiveTarget = "companies";
        effectiveCompanyIds = (g && g.company_ids) || [];
      }
      // Resolve email audience (independent). "same" → reuse portal audience;
      // otherwise ship only the picked subset.
      let outEmailClientIds = [];
      let outEmailCompanyIds = [];
      let outEmailGroupId = null;
      if (sendEmail) {
        if (emailTarget === "companies") {
          outEmailCompanyIds = Array.from(emailCompanyIds);
        } else if (emailTarget === "users") {
          outEmailClientIds = Array.from(emailClientIds);
        } else if (emailTarget === "group") {
          outEmailGroupId = emailGroupId;
        }
        // emailTarget === "same" → leave all empty; backend falls back to portal audience.
      }
      await onConfirm({
        target: effectiveTarget,
        company_ids: effectiveCompanyIds,
        client_ids: target === "users" ? Array.from(clientIds) : [],
        group_id: target === "group" ? groupId : null,
        send_notification: sendNotif,
        send_email: sendEmail,
        email_subject: sendEmail ? emailSubject.trim() : "",
        email_message: sendEmail ? emailMessage.trim() : "",
        email_client_ids: outEmailClientIds,
        email_company_ids: outEmailCompanyIds,
        email_group_id: outEmailGroupId,
      });
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center bg-slate-950/70 backdrop-blur-sm p-4 overflow-y-auto"
      data-testid="publish-target-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl mt-16 overflow-hidden border border-slate-200">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-center gap-2">
            <Send className="text-blue-600" size={18} />
            <h3 className="font-display font-bold text-slate-900">{title}</h3>
          </div>
          <button
            onClick={onCancel}
            className="p-1.5 rounded-lg hover:bg-white/60"
            data-testid="publish-target-close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {message && (
            <div className="text-sm text-slate-600">{message}</div>
          )}
          <div className="space-y-2">
            <label
              className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
                target === "all"
                  ? "border-blue-500 bg-blue-50/50"
                  : "border-slate-200 hover:bg-slate-50"
              }`}
              data-testid="target-all"
            >
              <input
                type="radio"
                name="publish-target"
                value="all"
                checked={target === "all"}
                onChange={() => setTarget("all")}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-medium text-slate-900 flex items-center gap-1.5">
                  <Users size={14} /> Tutti i clienti
                </div>
                <div className="text-xs text-slate-500">
                  Visibile a tutte le aziende clienti attive.
                </div>
              </div>
            </label>

            <label
              className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
                target === "companies"
                  ? "border-blue-500 bg-blue-50/50"
                  : "border-slate-200 hover:bg-slate-50"
              }`}
              data-testid="target-companies"
            >
              <input
                type="radio"
                name="publish-target"
                value="companies"
                checked={target === "companies"}
                onChange={() => setTarget("companies")}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-medium text-slate-900 flex items-center gap-1.5">
                  <Building2 size={14} /> Aziende selezionate
                </div>
                <div className="text-xs text-slate-500">
                  Solo gli utenti delle aziende selezionate potranno vedere questo contenuto.
                </div>
              </div>
            </label>

            <label
              className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
                target === "users"
                  ? "border-blue-500 bg-blue-50/50"
                  : "border-slate-200 hover:bg-slate-50"
              }`}
              data-testid="target-users"
            >
              <input
                type="radio"
                name="publish-target"
                value="users"
                checked={target === "users"}
                onChange={() => setTarget("users")}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-medium text-slate-900 flex items-center gap-1.5">
                  <User size={14} /> Utenti selezionati
                </div>
                <div className="text-xs text-slate-500">
                  Scegli singoli utenti raggruppati per azienda. Solo loro riceveranno la notifica/email.
                </div>
              </div>
            </label>

            <label
              className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
                target === "group"
                  ? "border-blue-500 bg-blue-50/50"
                  : "border-slate-200 hover:bg-slate-50"
              } ${groups.length === 0 ? "opacity-60" : ""}`}
              data-testid="target-group"
            >
              <input
                type="radio"
                name="publish-target"
                value="group"
                checked={target === "group"}
                onChange={() => setTarget("group")}
                className="mt-1"
                disabled={groups.length === 0}
              />
              <div className="flex-1">
                <div className="font-medium text-slate-900 flex items-center gap-1.5">
                  <Users2 size={14} /> Gruppo di clienti
                </div>
                <div className="text-xs text-slate-500">
                  {groups.length === 0
                    ? "Nessun gruppo creato — vai a Backoffice → Gruppi Clienti."
                    : "Seleziona un gruppo esistente; il contenuto sarà inviato a tutte le sue aziende."}
                </div>
              </div>
            </label>
          </div>

          {target === "companies" && (
            <div className="border border-slate-200 rounded-xl p-3 space-y-2 bg-slate-50/40">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Cerca azienda…"
                    className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-white"
                    data-testid="target-companies-search"
                  />
                </div>
                <button
                  type="button"
                  onClick={selectAll}
                  className="text-xs px-2 py-1 rounded-md bg-white border border-slate-200 hover:bg-slate-50"
                  data-testid="target-companies-select-all"
                >
                  Tutte
                </button>
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-xs px-2 py-1 rounded-md bg-white border border-slate-200 hover:bg-slate-50"
                  data-testid="target-companies-clear"
                >
                  Nessuna
                </button>
              </div>
              <div className="max-h-56 overflow-y-auto divide-y divide-slate-100 rounded-lg bg-white border border-slate-100">
                {loading ? (
                  <div className="p-4 flex items-center justify-center text-slate-500 gap-2 text-sm">
                    <Loader2 className="animate-spin" size={14} /> Caricamento…
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="p-4 text-sm text-slate-500 text-center">
                    Nessuna azienda trovata.
                  </div>
                ) : (
                  filtered.map((c) => (
                    <label
                      key={c.id}
                      className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer"
                      data-testid={`target-company-${c.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={companyIds.has(c.id)}
                        onChange={() => toggleCompany(c.id)}
                      />
                      <span className="flex-1 text-slate-800">{c.name}</span>
                      <span className="text-[10px] text-slate-400">
                        {c.users_count ?? 0} utenti
                      </span>
                    </label>
                  ))
                )}
              </div>
              <div className="text-xs text-slate-500">
                {companyIds.size} azienda{companyIds.size === 1 ? "" : "e"} selezionat{companyIds.size === 1 ? "a" : "e"}.
              </div>
            </div>
          )}

          {target === "users" && (
            <div className="border border-slate-200 rounded-xl p-3 space-y-2 bg-slate-50/40" data-testid="target-users-list">
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs text-slate-600 font-medium">
                  Seleziona gli utenti destinatari
                </div>
                <button
                  type="button"
                  onClick={clearAllUsers}
                  className="text-xs px-2 py-1 rounded-md bg-white border border-slate-200 hover:bg-slate-50"
                  data-testid="target-users-clear"
                >
                  Deseleziona tutti
                </button>
              </div>
              <div className="max-h-72 overflow-y-auto rounded-lg bg-white border border-slate-100 divide-y divide-slate-100">
                {loading ? (
                  <div className="p-4 flex items-center justify-center text-slate-500 gap-2 text-sm">
                    <Loader2 className="animate-spin" size={14} /> Caricamento…
                  </div>
                ) : usersByCompany.length === 0 ? (
                  <div className="p-4 text-sm text-slate-500 text-center">
                    Nessun utente disponibile.
                  </div>
                ) : (
                  usersByCompany.map(({ company, users: cu }) => {
                    const allSel = cu.every((u) => clientIds.has(u.id));
                    return (
                      <div key={company.id} className="px-3 py-2" data-testid={`target-users-company-${company.id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                            <Building2 size={12} /> {company.name}
                            <span className="text-slate-400 font-normal normal-case tracking-normal">({cu.length})</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => toggleCompanyUsers(cu)}
                            className="text-[10px] text-blue-600 hover:text-blue-800 font-semibold uppercase"
                            data-testid={`target-users-company-toggle-${company.id}`}
                          >
                            {allSel ? "Deseleziona" : "Seleziona tutti"}
                          </button>
                        </div>
                        <div className="space-y-0.5 pl-3">
                          {cu.map((u) => (
                            <label
                              key={u.id}
                              className="flex items-center gap-2 py-1 text-sm cursor-pointer hover:bg-slate-50 -mx-1 px-1 rounded"
                              data-testid={`target-user-${u.id}`}
                            >
                              <input
                                type="checkbox"
                                checked={clientIds.has(u.id)}
                                onChange={() => toggleUser(u.id)}
                              />
                              <span className="flex-1 text-slate-800">{u.full_name || u.email}</span>
                              <span className="text-[10px] text-slate-400">{u.email}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="text-xs text-slate-500">
                {clientIds.size} utent{clientIds.size === 1 ? "e" : "i"} selezionat{clientIds.size === 1 ? "o" : "i"}.
              </div>
            </div>
          )}

          {target === "group" && (
            <div className="border border-slate-200 rounded-xl p-3 space-y-2 bg-slate-50/40" data-testid="target-group-picker">
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-600">Seleziona il gruppo</label>
              <select
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:border-blue-500 outline-none"
                data-testid="target-group-select"
              >
                <option value="">— Scegli un gruppo —</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name} ({(g.companies || []).length} aziende, {g.users_total || 0} utenti)
                  </option>
                ))}
              </select>
              {groupId && (() => {
                const g = groups.find((x) => x.id === groupId);
                if (!g) return null;
                return (
                  <div className="text-xs text-slate-600 mt-2 pl-1" data-testid={`target-group-preview-${g.id}`}>
                    <div className="font-semibold text-slate-700 mb-1">Clienti del gruppo:</div>
                    <ul className="space-y-1 max-h-40 overflow-y-auto pl-1">
                      {(g.companies || []).map((c) => (
                        <li key={c.id}>
                          <span className="font-semibold text-slate-800">{c.name}</span>
                          <span className="text-slate-400 ml-1">({c.users.length} utenti)</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })()}
            </div>
          )}

          {showNotificationToggle && (
            <div className="space-y-2 pt-1">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendNotif}
                  onChange={(e) => setSendNotif(e.target.checked)}
                  data-testid="target-send-notification"
                />
                Invia una notifica ai clienti destinatari
              </label>
              <label
                className={`flex items-start gap-2 text-sm cursor-pointer rounded-lg px-2 py-1.5 -mx-2 ${
                  sendEmail ? "bg-amber-50 border border-amber-200" : ""
                }`}
                data-testid="target-send-email-label"
              >
                <input
                  type="checkbox"
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  className="mt-0.5"
                  data-testid="target-send-email"
                />
                <span className="flex-1">
                  <span className="text-slate-700">Invia anche <b>email</b> ai clienti destinatari</span>
                  <span className="block text-xs text-slate-500 mt-0.5">
                    Un&apos;email transazionale (branded Adybo) verrà inviata a chi ha le notifiche attive nel Profilo.
                  </span>
                </span>
              </label>
              {sendEmail && (
                <div className="space-y-2 pl-6 pt-1" data-testid="email-editor">
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-1">Oggetto email</label>
                    <input
                      type="text"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      placeholder="Oggetto (lascia vuoto per usare il default)"
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:border-blue-500 outline-none"
                      data-testid="email-subject-input"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-1">Messaggio email</label>
                    <textarea
                      value={emailMessage}
                      onChange={(e) => setEmailMessage(e.target.value)}
                      placeholder="Corpo dell'email (lascia vuoto per usare il default). Verrà inserito nel template branded con logo e CTA 'Apri sul portale'."
                      rows={4}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:border-blue-500 outline-none resize-y"
                      data-testid="email-message-input"
                    />
                    <div className="text-[10px] text-slate-400 mt-1">
                      Il testo verrà racchiuso nel template Adybo con header, logo e pulsante di accesso al portale.
                    </div>
                  </div>

                  {/* NEW — independent email audience picker */}
                  <div className="pt-2 border-t border-amber-200/60">
                    <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-1">
                      Destinatari email <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={emailTarget}
                      onChange={(e) => setEmailTarget(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:border-blue-500 outline-none"
                      data-testid="email-target-select"
                    >
                      <option value="">— Scegli chi riceverà l&apos;email —</option>
                      <option value="same">Stessi dell&apos;audience portale</option>
                      <option value="companies">Solo aziende specifiche</option>
                      <option value="users">Solo utenti specifici</option>
                      <option value="group" disabled={groups.length === 0}>
                        Solo un gruppo di clienti{groups.length === 0 ? " (nessun gruppo)" : ""}
                      </option>
                    </select>
                    <div className="text-[10px] text-slate-500 mt-1">
                      L&apos;audience portale (chi vede il contenuto) resta indipendente: puoi pubblicare a tutti ma inviare l&apos;email solo ad alcuni.
                    </div>

                    {emailAudienceStats.optedOut > 0 && (
                      <div
                        className="mt-2 p-2 rounded-md bg-amber-50 border border-amber-300 text-[11px] text-amber-800 flex items-start gap-1.5"
                        data-testid="email-optout-warning"
                      >
                        <span>⚠️</span>
                        <span>
                          <b>{emailAudienceStats.optedOut}</b> {emailAudienceStats.optedOut === 1 ? "destinatario ha" : "destinatari hanno"} disattivato la ricezione delle email dal proprio Profilo e non {emailAudienceStats.optedOut === 1 ? "riceverà" : "riceveranno"} questa comunicazione (su {emailAudienceStats.total} selezionati). Verrà registrato nello Storico Email.
                        </span>
                      </div>
                    )}

                    {emailTarget === "companies" && (
                      <div className="mt-2 border border-slate-200 rounded-lg p-2 bg-white space-y-2" data-testid="email-companies-picker">
                        <div className="relative">
                          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input
                            type="text"
                            value={emailCompanySearch}
                            onChange={(e) => setEmailCompanySearch(e.target.value)}
                            placeholder="Cerca azienda…"
                            className="w-full pl-7 pr-2 py-1 text-xs border border-slate-200 rounded-md bg-white"
                            data-testid="email-companies-search"
                          />
                        </div>
                        <div className="max-h-40 overflow-y-auto divide-y divide-slate-100">
                          {companies
                            .filter((c) => (c.name || "").toLowerCase().includes(emailCompanySearch.trim().toLowerCase()))
                            .map((c) => (
                              <label
                                key={c.id}
                                className="flex items-center gap-2 py-1 text-xs cursor-pointer hover:bg-slate-50 px-1"
                                data-testid={`email-company-${c.id}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={emailCompanyIds.has(c.id)}
                                  onChange={() => setEmailCompanyIds((prev) => {
                                    const n = new Set(prev);
                                    if (n.has(c.id)) n.delete(c.id); else n.add(c.id);
                                    return n;
                                  })}
                                />
                                <span className="flex-1 text-slate-800">{c.name}</span>
                                <span className="text-[10px] text-slate-400">{c.users_count ?? 0} utenti</span>
                              </label>
                            ))}
                        </div>
                        <div className="text-[10px] text-slate-500">{emailCompanyIds.size} aziend{emailCompanyIds.size === 1 ? "a" : "e"} selezionat{emailCompanyIds.size === 1 ? "a" : "e"}.</div>
                      </div>
                    )}

                    {emailTarget === "users" && (
                      <div className="mt-2 border border-slate-200 rounded-lg p-2 bg-white space-y-2" data-testid="email-users-picker">
                        <div className="relative">
                          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input
                            type="text"
                            value={emailUserSearch}
                            onChange={(e) => setEmailUserSearch(e.target.value)}
                            placeholder="Cerca utente (nome/email/azienda)…"
                            className="w-full pl-7 pr-2 py-1 text-xs border border-slate-200 rounded-md bg-white"
                            data-testid="email-users-search"
                          />
                        </div>
                        <div className="max-h-52 overflow-y-auto rounded-md">
                          {usersByCompany.map(({ company, users: cu }) => {
                            const q = emailUserSearch.trim().toLowerCase();
                            const visible = q
                              ? cu.filter((u) => `${u.full_name || ""} ${u.email || ""} ${company.name || ""}`.toLowerCase().includes(q))
                              : cu;
                            if (visible.length === 0) return null;
                            return (
                              <div key={company.id} className="py-1">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1 px-1">
                                  <Building2 size={10} /> {company.name}
                                  <span className="text-slate-400 font-normal normal-case tracking-normal">({visible.length})</span>
                                </div>
                                {visible.map((u) => (
                                  <label
                                    key={u.id}
                                    className="flex items-center gap-2 py-0.5 pl-3 text-xs cursor-pointer hover:bg-slate-50 rounded"
                                    data-testid={`email-user-${u.id}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={emailClientIds.has(u.id)}
                                      onChange={() => setEmailClientIds((prev) => {
                                        const n = new Set(prev);
                                        if (n.has(u.id)) n.delete(u.id); else n.add(u.id);
                                        return n;
                                      })}
                                    />
                                    <span className="flex-1 text-slate-800">{u.full_name || u.email}</span>
                                    <span className="text-[10px] text-slate-400">{u.email}</span>
                                  </label>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                        <div className="text-[10px] text-slate-500">{emailClientIds.size} utent{emailClientIds.size === 1 ? "e" : "i"} selezionat{emailClientIds.size === 1 ? "o" : "i"}.</div>
                      </div>
                    )}

                    {emailTarget === "group" && (
                      <div className="mt-2 border border-slate-200 rounded-lg p-2 bg-white space-y-2" data-testid="email-group-picker">
                        <select
                          value={emailGroupId}
                          onChange={(e) => setEmailGroupId(e.target.value)}
                          className="w-full px-2 py-1 text-xs border border-slate-200 rounded-md bg-white"
                          data-testid="email-group-select"
                        >
                          <option value="">— Scegli un gruppo —</option>
                          {groups.map((g) => (
                            <option key={g.id} value={g.id}>
                              {g.name} ({(g.companies || []).length} aziende, {g.users_total || 0} utenti)
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-100 bg-slate-50/50">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-sm rounded-lg bg-white border border-slate-200 hover:bg-slate-100"
            data-testid="publish-target-cancel"
          >
            Annulla
          </button>
          <button
            onClick={submit}
            disabled={!canConfirm || busy}
            className="btn-brand flex items-center gap-2 text-sm !py-1.5 !px-3 disabled:opacity-50"
            data-testid="publish-target-confirm"
          >
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
